import { useState, useCallback, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import type { Node as ReactFlowNode } from 'reactflow'
import { type MentionCandidate, buildCandidates } from './mentionUtils'
import { useProjectId } from '../../contexts/ProjectIdContext'
import {
  createMentionChipElement,
  serializeNodeText,
  getPlainTextFromEditor,
  getReferencedNodeIdsFromEditor,
  selectionInsideEditor,
  placeCaretAtEnd,
  hydrateContent,
  MentionDropdownPortal,
} from './contentEditableUtils'

type ChatInputStatus = 'idle' | 'running' | 'polling' | 'pending_questions'

/** Attachment for chat messages (RFC 0054) */
export interface ChatAttachment {
  type: 'image'
  file?: File
  previewUrl: string
  path?: string  // Path to uploaded asset (when selected from gallery)
  name?: string
}

/* ------------------------------------------------------------------ */
/*  ChatInput                                                          */
/* ------------------------------------------------------------------ */

export interface ChatInputHandle {
  setText: (text: string) => void
  addMention: (nodeId: string) => void
}


interface ChatInputProps {
  onSend: (message: string, attachments?: ChatAttachment[], referencedNodeIds?: string[]) => void
  /** RFC 0061: Callback to stop running execution */
  onStop?: () => void
  disabled?: boolean
  placeholder?: string
  /** Status for the send button indicator (RFC 0036) */
  status?: ChatInputStatus
  /** Project ID for image picker gallery (RFC 0054) */
  projectId?: string
  /** Canvas nodes for @mention autocomplete */
  canvasNodes?: ReactFlowNode[]
  /** Called when a mention pill is clicked -- used to focus the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Callback when user selects a file to upload from Director. Creates an upload node on the canvas. */
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ onSend, onStop, disabled = false, placeholder = 'Ask about your panels...', status = 'idle', canvasNodes, onMentionClick, onUploadFile }, ref) {
  const projectId = useProjectId()
  const [isFocused, setIsFocused] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const editorRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
  const composingRef = useRef(false)

  // Upload flyover state
  const [showUploadMenu, setShowUploadMenu] = useState(false)
  const uploadMenuRef = useRef<HTMLDivElement>(null)
  const uploadButtonRef = useRef<HTMLButtonElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)

  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionFilter, setMentionFilter] = useState('')
  const [mentionHighlight, setMentionHighlight] = useState(0)

  // Build mention candidates (with thumbnailPath from canvas-v2 mentionUtils)
  const candidates = useMemo(() => canvasNodes ? buildCandidates(canvasNodes) : [], [canvasNodes])
  const filteredCandidates = mentionFilter
    ? candidates.filter(
        (c) =>
          c.label.toLowerCase().includes(mentionFilter.toLowerCase()) ||
          c.type.toLowerCase().includes(mentionFilter.toLowerCase()),
      )
    : candidates

  // Track whether editor has content for send button state
  const [hasContent, setHasContent] = useState(false)

  const closeDropdown = useCallback(() => {
    anchorRef.current = null
    setShowMentionDropdown(false)
    setMentionFilter('')
    setMentionHighlight(0)
  }, [])

  const adjustHeight = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    editor.style.height = 'auto'
    if (editor.textContent || editor.querySelector('[data-mention-pill]')) {
      const maxHeight = 160
      const newHeight = Math.min(Math.max(editor.scrollHeight, 24), maxHeight)
      editor.style.height = `${newHeight}px`
    }
  }, [])

  const emitContentState = useCallback(() => {
    const editor = editorRef.current
    const plainText = getPlainTextFromEditor(editor)
    setHasContent(plainText.length > 0)
    adjustHeight()
  }, [adjustHeight])

  useImperativeHandle(ref, () => ({
    setText: (text: string) => {
      const editor = editorRef.current
      if (!editor) return
      hydrateContent(editor, text, candidates, projectId)
      emitContentState()
      placeCaretAtEnd(editor)
    },
    addMention: (nodeId: string) => {
      const editor = editorRef.current
      if (!editor) return

      // Find the candidate
      const candidate = candidates.find(c => c.id === nodeId)
      if (!candidate) return

      // Skip if already mentioned (dedup)
      const existing = getReferencedNodeIdsFromEditor(editor)
      if (existing.includes(nodeId)) return

      // Insert chip at end
      const selection = window.getSelection()
      if (!selection || !selectionInsideEditor(editor, selection)) {
        placeCaretAtEnd(editor)
      }

      // Insert a space before the chip if needed
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)

        // Check if we need a leading space
        const editorText = editor.textContent ?? ''
        if (editorText.length > 0 && !/\s$/.test(editorText)) {
          const spaceNode = document.createTextNode(' ')
          range.insertNode(spaceNode)
          range.setStartAfter(spaceNode)
          range.collapse(true)
        }

        const chip = createMentionChipElement(candidate, projectId)
        range.insertNode(chip)
        range.setStartAfter(chip)
        range.collapse(true)

        // Add trailing space
        const trailingSpace = document.createTextNode(' ')
        range.insertNode(trailingSpace)
        range.setStartAfter(trailingSpace)
        range.collapse(true)

        sel.removeAllRanges()
        sel.addRange(range)
      }

      emitContentState()
      editor.focus()
    },
  }), [candidates, projectId, emitContentState])

  // Reset isStopping when status changes from running/polling to something else
  useEffect(() => {
    if (status !== 'running' && status !== 'polling') {
      setIsStopping(false)
    }
  }, [status])

  // Close upload menu on outside click
  useEffect(() => {
    if (!showUploadMenu) return
    const handleClick = (e: MouseEvent) => {
      if (
        uploadMenuRef.current &&
        !uploadMenuRef.current.contains(e.target as globalThis.Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(e.target as globalThis.Node)
      ) {
        setShowUploadMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showUploadMenu])

  // Handle file selection from hidden inputs
  const handleUploadFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'audio') => {
    const file = e.target.files?.[0]
    if (file) {
      onUploadFile?.(file, type)
    }
    // Reset the input so the same file can be selected again
    e.target.value = ''
    setShowUploadMenu(false)
  }, [onUploadFile])

  // Adjust height on mount
  useEffect(() => {
    adjustHeight()
  }, [adjustHeight])

  const handleSend = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    const plainText = getPlainTextFromEditor(editor)
    const trimmed = plainText.trim()
    if (!trimmed || disabled) return

    // Serialize chips as @[[nodeId]] markers
    const serialized = serializeNodeText(editor, editor).replace(/\u00A0/g, ' ').trim()
    const referencedNodeIds = getReferencedNodeIdsFromEditor(editor)

    onSend(
      serialized,
      undefined,
      referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
    )

    // Clear editor
    editor.innerHTML = ''
    setHasContent(false)
    adjustHeight()
  }, [onSend, disabled, adjustHeight])

  // Mention trigger detection
  const syncMentionTrigger = useCallback(() => {
    const editor = editorRef.current
    if (!editor || candidates.length === 0 || composingRef.current) {
      closeDropdown()
      return
    }

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
      closeDropdown()
      return
    }

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    if (textNode.nodeType !== Node.TEXT_NODE) {
      closeDropdown()
      return
    }

    const text = textNode.textContent ?? ''
    const cursorPos = range.startOffset

    let atPos = -1
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = text[i]
      if (char === '@') {
        atPos = i
        break
      }
      if (char === ' ' || char === '\n' || char === '\r') break
    }

    if (atPos >= 0 && (atPos === 0 || /\s/.test(text[atPos - 1] ?? ''))) {
      anchorRef.current = { node: textNode, offset: atPos }
      const query = text.substring(atPos + 1, cursorPos)
      setMentionFilter(query)
      setShowMentionDropdown(true)
      setMentionHighlight(0)

      // Close if no candidates match
      const queryLower = query.toLowerCase()
      const hasMatch = queryLower === '' || candidates.some(
        (c) => c.label.toLowerCase().includes(queryLower) || c.type.toLowerCase().includes(queryLower),
      )
      if (!hasMatch) {
        closeDropdown()
      }
      return
    }

    closeDropdown()
  }, [candidates, closeDropdown])

  // Insert a mention chip
  const insertMention = useCallback((candidate: MentionCandidate) => {
    const editor = editorRef.current
    const anchor = anchorRef.current
    if (!editor || !anchor) {
      closeDropdown()
      return
    }

    const anchorNode = anchor.node
    if (anchorNode.nodeType !== Node.TEXT_NODE || !anchorNode.parentNode) {
      closeDropdown()
      return
    }

    const fullText = anchorNode.textContent ?? ''
    const mentionLength = 1 + mentionFilter.length
    const before = fullText.substring(0, anchor.offset)
    const after = fullText.substring(anchor.offset + mentionLength)
    const parent = anchorNode.parentNode

    const beforeNode = document.createTextNode(before)
    const chip = createMentionChipElement(candidate, projectId)
    const trailingSpacer = after.length === 0 || /^\s/.test(after) ? '' : ' '
    const afterNode = document.createTextNode(`${trailingSpacer}${after}`)

    parent.insertBefore(beforeNode, anchorNode)
    parent.insertBefore(chip, anchorNode)
    parent.insertBefore(afterNode, anchorNode)
    parent.removeChild(anchorNode)

    // Place cursor after chip
    const selection = window.getSelection()
    if (selection) {
      const range = document.createRange()
      range.setStart(afterNode, trailingSpacer.length)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    emitContentState()
    closeDropdown()
    editor.focus()
  }, [mentionFilter, closeDropdown, emitContentState, projectId])

  // Chip removal
  const removeChip = useCallback((chip: HTMLElement) => {
    chip.remove()
    const editor = editorRef.current
    if (editor && editor.textContent === '') {
      editor.innerHTML = ''
    }
    emitContentState()
    if (editor) placeCaretAtEnd(editor)
  }, [emitContentState])

  const removeAdjacentChip = useCallback((direction: 'backward' | 'forward') => {
    const editor = editorRef.current
    if (!editor) return false

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    if (!range.collapsed) return false

    let adjacentNode: globalThis.Node | null = null
    if (range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text
      if (direction === 'backward' && range.startOffset === 0) {
        adjacentNode = textNode.previousSibling
      } else if (
        direction === 'forward' &&
        range.startOffset === (textNode.textContent ?? '').length
      ) {
        adjacentNode = textNode.nextSibling
      }
    } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
      const container = range.startContainer as HTMLElement
      const childIndex =
        direction === 'backward' ? range.startOffset - 1 : range.startOffset
      adjacentNode = container.childNodes[childIndex] ?? null
    }

    if (
      adjacentNode instanceof HTMLElement &&
      adjacentNode.dataset.mentionPill === 'true'
    ) {
      removeChip(adjacentNode)
      return true
    }

    return false
  }, [removeChip])

  // Input handler
  const handleInput = useCallback(() => {
    if (composingRef.current) return
    emitContentState()
    syncMentionTrigger()
  }, [emitContentState, syncMentionTrigger])

  const handleCompositionStart = useCallback(() => {
    composingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false
    handleInput()
  }, [handleInput])

  // Serialize selection for copy/cut
  const serializeSelection = useCallback((): string | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    const fragment = range.cloneContents()
    const container = document.createElement('div')
    container.appendChild(fragment)

    const serialized = serializeNodeText(container, container)
    return serialized || null
  }, [])

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)
  }, [serializeSelection])

  const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      selection.getRangeAt(0).deleteContents()
    }

    const editor = editorRef.current
    if (editor && editor.textContent === '') {
      editor.innerHTML = ''
    }
    emitContentState()
  }, [emitContentState, serializeSelection])

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const editor = editorRef.current
    if (!editor) return

    const text = e.clipboardData.getData('text/plain')
    if (!text) return

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!range.collapsed) {
      range.deleteContents()
    }

    const MARKER_RE = /@\[\[([^\]]+)\]\]/g
    const hasMarkers = MARKER_RE.test(text)

    if (hasMarkers) {
      MARKER_RE.lastIndex = 0
      const frag = document.createDocumentFragment()
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = MARKER_RE.exec(text)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
        }
        const nodeId = match[1]
        const candidate = candidates.find((c) => c.id === nodeId)
        if (candidate) {
          frag.appendChild(createMentionChipElement(candidate, projectId))
        } else {
          frag.appendChild(document.createTextNode(match[0]))
        }
        lastIndex = match.index + match[0].length
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)))
      }

      const lastChild = frag.lastChild
      range.insertNode(frag)

      if (lastChild) {
        range.setStartAfter(lastChild)
        range.collapse(true)
      } else {
        range.collapse(false)
      }
      selection.removeAllRanges()
      selection.addRange(range)
    } else {
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    emitContentState()
  }, [candidates, emitContentState, projectId])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Ignore keystrokes during IME composition
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return

    // Mention dropdown navigation
    if (showMentionDropdown && filteredCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionHighlight(prev => {
          const next = (prev + 1) % filteredCandidates.length
          onMentionClick?.(filteredCandidates[next].id)
          return next
        })
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionHighlight(prev => {
          const next = (prev - 1 + filteredCandidates.length) % filteredCandidates.length
          onMentionClick?.(filteredCandidates[next].id)
          return next
        })
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(filteredCandidates[mentionHighlight])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        closeDropdown()
        return
      }
    }

    // Chip removal via Backspace/Delete
    if (e.key === 'Backspace' && removeAdjacentChip('backward')) {
      e.preventDefault()
      return
    }
    if (e.key === 'Delete' && removeAdjacentChip('forward')) {
      e.preventDefault()
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend, showMentionDropdown, filteredCandidates, mentionHighlight, insertMention, onMentionClick, closeDropdown, removeAdjacentChip])

  // Click handling for chips
  const handleEditorClick = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (!target) return

    const removeButton = target.closest('[data-mention-chip-remove]') as HTMLElement | null
    if (removeButton) {
      event.preventDefault()
      event.stopPropagation()
      const chip = removeButton.closest('[data-mention-pill]') as HTMLElement | null
      if (chip) removeChip(chip)
      return
    }

    const chipLabel = target.closest('[data-mention-chip-label]') as HTMLElement | null
    if (chipLabel) {
      const chip = chipLabel.closest('[data-mention-pill]') as HTMLElement | null
      if (chip?.dataset.nodeId) {
        event.preventDefault()
        event.stopPropagation()
        onMentionClick?.(chip.dataset.nodeId)
      }
    }
  }, [onMentionClick, removeChip])

  // Close mention dropdown on outside click
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const editor = editorRef.current
      const dropdown = dropdownRef.current
      const target = event.target as globalThis.Node | null
      if (!editor || !target) return
      if (!editor.contains(target) && !(dropdown && dropdown.contains(target))) {
        closeDropdown()
      }
    }

    const editor = editorRef.current
    if (!editor) return

    editor.addEventListener('click', handleEditorClick)
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      editor.removeEventListener('click', handleEditorClick)
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [closeDropdown, handleEditorClick])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!showMentionDropdown || !dropdownRef.current) return
    const items = dropdownRef.current.querySelectorAll('[data-mention-item]')
    items[mentionHighlight]?.scrollIntoView({ block: 'nearest' })
  }, [mentionHighlight, showMentionDropdown])


  const canSend = hasContent && !disabled

  return (
    <div className="p-3 border-t border-border-subtle">
      {/* Hidden file inputs for upload flyover */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUploadFileSelect(e, 'image')}
        className="hidden"
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={(e) => handleUploadFileSelect(e, 'video')}
        className="hidden"
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => handleUploadFileSelect(e, 'audio')}
        className="hidden"
      />

      {/* Main input container */}
      <div
        className={`
          relative rounded-lg border transition-colors duration-150
          ${isFocused
            ? 'border-border bg-surface-1'
            : 'border-border-subtle bg-surface-0 hover:border-border'
          }
          ${disabled ? 'opacity-50' : ''}
        `}
      >
        {/* Portal-based mention dropdown */}
        {showMentionDropdown && (
          <MentionDropdownPortal
            candidates={filteredCandidates}
            highlightIndex={mentionHighlight}
            projectId={projectId}
            editorRef={editorRef}
            dropdownRef={dropdownRef}
            onSelect={insertMention}
            onHighlight={setMentionHighlight}
          />
        )}

        {/* contentEditable editor */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-label={placeholder}
          aria-multiline="true"
          aria-autocomplete="list"
          aria-expanded={showMentionDropdown}
          aria-controls={showMentionDropdown ? 'chat-mention-dropdown' : undefined}
          data-testid="chat-input-editor"
          data-placeholder={placeholder}
          className={`chat-input__editor w-full bg-transparent px-4 pt-3 pb-2 text-sm text-foreground focus:outline-none overflow-y-auto whitespace-pre-wrap break-words ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
          style={{ minHeight: '24px', maxHeight: '160px' }}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onCopy={handleCopy}
          onCut={handleCut}
          onPaste={handlePaste}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />

        <style>{`
          .chat-input__editor:empty::before {
            content: attr(data-placeholder);
            color: var(--text-muted, #6a6a8a);
            pointer-events: none;
          }
        `}</style>

        {/* Action bar */}
        <div className="flex items-center justify-between px-2 pb-2">
          {/* Left side - Upload + @ buttons */}
          <div className="flex items-center gap-1 relative">
            {/* Upload button with flyover popover */}
            <button
              ref={uploadButtonRef}
              type="button"
              disabled={disabled}
              onClick={() => setShowUploadMenu(prev => !prev)}
              className="p-2 rounded-md text-text-muted hover:text-text-secondary hover:bg-surface-2 transition-colors disabled:opacity-50"
              title="Upload file"
              data-testid="upload-button"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </button>

            {/* Upload flyover popover */}
            {showUploadMenu && (
              <div
                ref={uploadMenuRef}
                className="absolute bottom-full left-0 mb-2 bg-surface-1 border border-border-subtle rounded-lg shadow-lg z-50 min-w-[160px] py-1"
                data-testid="upload-menu"
              >
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  onClick={() => imageInputRef.current?.click()}
                  data-testid="upload-image-option"
                >
                  <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Upload Image
                </button>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  onClick={() => videoInputRef.current?.click()}
                  data-testid="upload-video-option"
                >
                  <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Upload Video
                </button>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-surface-2 transition-colors"
                  onClick={() => audioInputRef.current?.click()}
                  data-testid="upload-audio-option"
                >
                  <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  Upload Audio
                </button>
              </div>
            )}

            {/* @ mention button */}
            {canvasNodes && canvasNodes.length > 0 && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  const editor = editorRef.current
                  if (!editor) return

                  // Place cursor inside editor if not already
                  const selection = window.getSelection()
                  if (!selection || !selectionInsideEditor(editor, selection)) {
                    placeCaretAtEnd(editor)
                  }

                  // Check if we need a space before @
                  const sel = window.getSelection()
                  if (!sel || sel.rangeCount === 0) return
                  const range = sel.getRangeAt(0)

                  const editorText = editor.textContent ?? ''
                  const needsSpace = editorText.length > 0 && !/\s$/.test(editorText)

                  const textToInsert = needsSpace ? ' @' : '@'
                  const textNode = document.createTextNode(textToInsert)
                  range.insertNode(textNode)
                  // Keep cursor inside the text node so syncMentionTrigger()
                  // finds a TEXT_NODE (not the parent element).
                  range.setStart(textNode, textToInsert.length)
                  range.setEnd(textNode, textToInsert.length)
                  sel.removeAllRanges()
                  sel.addRange(range)

                  editor.focus()
                  // Trigger mention detection after the browser
                  // has committed the selection update.
                  emitContentState()
                  requestAnimationFrame(() => {
                    syncMentionTrigger()
                  })
                }}
                className="p-2 rounded-md text-text-muted hover:text-text-secondary hover:bg-surface-2 transition-colors disabled:opacity-50"
                title="Reference a canvas node (@mention)"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-4 4m4-4a4 4 0 01-4 4m4-4h.01M12 16v.01M8 12h.01M12 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>

          {/* Right side - send button with status indicator */}
          {/* RFC 0061: Stop button when running */}
          {status === 'running' || status === 'polling' ? (
            <button
              onClick={() => {
                setIsStopping(true)
                onStop?.()
              }}
              disabled={isStopping}
              className={`p-2 rounded-md transition-colors flex items-center gap-2 ${
                isStopping
                  ? 'bg-destructive/10 text-destructive/50 cursor-not-allowed'
                  : 'bg-destructive/20 text-destructive hover:bg-destructive/30'
              }`}
              title={isStopping ? "Stopping..." : "Stop execution"}
            >
              {/* Stop icon (square) */}
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
              {isStopping ? (
                <span className="text-xs">stopping...</span>
              ) : status === 'polling' ? (
                <span className="text-xs opacity-70">polling</span>
              ) : null}
            </button>
          ) : status === 'pending_questions' ? (
            <div
              className="p-2 rounded-md bg-warning/10 text-warning"
              title="Answer the questions above to continue"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`
                p-2 rounded-md transition-colors duration-150
                ${canSend
                  ? 'bg-primary text-foreground hover:bg-primary/90'
                  : 'bg-surface-2 text-text-muted cursor-not-allowed'
                }
              `}
              title="Send message"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Helper text */}
      <div className="mt-2 text-xs text-text-muted text-center">
        <span>Enter</span> to send · <span>Shift+Enter</span> for new line
        {canvasNodes && canvasNodes.length > 0 && (
          <> · <span>@</span> to mention a node</>
        )}
      </div>
    </div>
  )
})
